import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { RotateCw } from "lucide-react";

import { PageShell } from "../components/PageShell";
import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Input } from "../../../ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  useNotifications,
  useRetryNotification,
  type NotificationRow,
} from "../../../utils/notification-admin/api";

// Display labels for every real source_system value (used wherever a notification's
// source is shown, e.g. the table column and detail modal) -- Grafana included even
// though it isn't one of the selectable product-filter options below.
const SOURCE_LABELS = [
  { value: "grafana-webhook", label: "Grafana" },
  { value: "zammad-webhook", label: "Support" },
  { value: "discourse-webhook", label: "Agora" },
  { value: "outline-webhook", label: "Atlas" },
];

// Product filter shows business-facing product names; the underlying value sent to
// the backend query is still the real source_system (e.g. Support -> zammad-webhook).
const PRODUCT_FILTER_OPTIONS = [
  { value: "zammad-webhook", label: "Support" },
  { value: "discourse-webhook", label: "Agora" },
  { value: "outline-webhook", label: "Atlas" },
];

function sourceLabel(sourceSystem: string): string {
  return SOURCE_LABELS.find((o) => o.value === sourceSystem)?.label ?? sourceSystem;
}

export default function NotificationList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const sourceSystem = searchParams.get("source_system") ?? undefined;
  const search = searchParams.get("search") ?? undefined;
  const page = Number(searchParams.get("page") ?? "1");
  const limit = 20;

  const [searchInput, setSearchInput] = useState(search ?? "");
  const [detailRow, setDetailRow] = useState<NotificationRow | null>(null);

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.set("page", "1");
    setSearchParams(next);
  };

  const { data, isLoading, isFetching } = useNotifications({
    source_system: sourceSystem,
    search,
    page,
    limit,
  });
  const retry = useRetryNotification();

  const columns = useMemo<ColumnDef<NotificationRow>[]>(
    () => [
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground tabular-nums">
            {format(new Date(row.original.created_at), "d MMM yyyy, HH:mm")}
          </span>
        ),
      },
      {
        accessorKey: "title",
        header: "Notification",
        cell: ({ row }) => (
          <div className="max-w-md">
            <div className="font-medium truncate">{row.original.title}</div>
            <div className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
              {row.original.event_type}
            </div>
          </div>
        ),
      },
      {
        accessorKey: "source_system",
        header: "Product",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {sourceLabel(row.original.source_system)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setDetailRow(row.original)}>
              View
            </Button>
            {row.original.status === "FAILED" && (
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                disabled={retry.isPending}
                onClick={() => retry.mutate(row.original.id)}
              >
                <RotateCw className="h-3.5 w-3.5" />
                Retry
              </Button>
            )}
          </div>
        ),
      },
    ],
    [retry],
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = data?.pagination.total_pages ?? 1;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Athena · Notification"
        title="Notifications"
        subtitle="Every notification created by Grafana, Zammad, Outline, and Discourse integrations."
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search title or body…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setParam("search", searchInput || undefined);
          }}
          onBlur={() => setParam("search", searchInput || undefined)}
          className="w-64"
        />

        <Select
          value={sourceSystem ?? "all"}
          onValueChange={(v) => setParam("source_system", v === "all" ? undefined : v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Product Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {PRODUCT_FILTER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground">Refreshing…</span>
        )}
      </div>

      <div className="mt-5 overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-10">
                  Loading…
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-10">
                  No notifications match these filters.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-4 justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) setParam("page", String(page - 1));
                }}
                className={page <= 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#" isActive>
                {page} / {totalPages}
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) setParam("page", String(page + 1));
                }}
                className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={detailRow !== null} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detailRow?.title}</DialogTitle>
            <DialogDescription>
              {detailRow && sourceLabel(detailRow.source_system)}
            </DialogDescription>
          </DialogHeader>
          {detailRow && (
            <div className="space-y-3 text-sm">
              {detailRow.body && (
                <p className="text-sm text-foreground whitespace-pre-wrap">{detailRow.body}</p>
              )}
              {detailRow.recipient && detailRow.recipient.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Recipients: {detailRow.recipient.join(", ")}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
